import * as React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { useLocale } from 'app/components/Intl';
import { isNil, parseDate, serializeDate } from 'app/utils';

import type { RegulationsField } from './RegulationsField.types';
import { useRegulationsFieldValue } from './useRegulationsFieldValue';

export type RegulationsDateFieldProps = {
  field: RegulationsField;
  className?: string;
  variant?: string;
};

const toDate = (value: any, localDate: boolean): Date | null => {
  if (isNil(value) || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    return parseDate(value, localDate);
  }
  return value instanceof Date ? value : new Date(value);
};

const RegulationsDateFieldComponent = ({ field, className, variant }: RegulationsDateFieldProps) => {
  const { locale } = useLocale();
  const {
    name,
    value,
    defaultValue,
    type = 'date',
    options = {},
    required = false,
    disabled = false,
    fullWidth = true,
  } = field;
  const { startOfDay, endOfDay, localDate = true } = options;

  const initialValue = React.useMemo(() => {
    const source = isNil(value) ? defaultValue : value;
    return isNil(source) ? '' : source;
  }, [value, defaultValue]);

  const [currentValue, commit] = useRegulationsFieldValue(name, initialValue);

  const dateValue = React.useMemo(() => toDate(currentValue, localDate), [currentValue, localDate]);

  const onChange = React.useCallback(
    (newValue: Date | null) => {
      commit(serializeDate(newValue, localDate, startOfDay, endOfDay));
    },
    [commit, localDate, startOfDay, endOfDay],
  );

  const size = variant === 'slim' ? 'small' : undefined;
  const viewProps = React.useMemo(
    () => (type === 'dayAndMonth' ? { format: 'dd/MM', views: ['month', 'day'] as const } : {}),
    [type],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <MuiDatePicker
        className={className}
        value={dateValue}
        onChange={onChange}
        disabled={disabled}
        {...viewProps}
        slotProps={{
          textField: {
            size,
            fullWidth,
            required,
          },
        }}
      />
    </LocalizationProvider>
  );
};

export const RegulationsDateField = React.memo(RegulationsDateFieldComponent);
